import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import ProgressBar from 'tio-ui/components/progress-bar';
import Text from 'tio-ui/components/text';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import Collapsible from 'tio-ui/components/collapsible';
import { PhArrowRight, PhCaretDown, PhClockCountdown } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';

interface LoanProgress {
  id: string;
  name: string;
  servicer: string | undefined;
  displayName: string;
  qualifyingPayments: number;
  totalRequired: number;
  percentage: number;
  hasProgress: boolean;
}

interface QualifyingPaymentTrackerSignature {
  Args: {
    accounts?: AccountModel[];
  };
  Element: HTMLDivElement;
}

class QualifyingPaymentTracker extends Component<QualifyingPaymentTrackerSignature> {
  readonly TOTAL_REQUIRED_PAYMENTS = 120;

  @tracked showAllLoans = false;

  get nsldsAccounts() {
    return (this.args.accounts ?? []).filter((account) => account.hasNsldsPslfData);
  }

  /** Derive "last updated" from the most recent NSLDS timestamp across account-level provider-success-at and loan-level nsldsUpdatedAt values */
  get nsldsLastUpdated() {
    const nsldsAccounts = this.nsldsAccounts;
    if (nsldsAccounts.length === 0) return null;

    const dates = nsldsAccounts
      .flatMap((account) => [
        account.observation?.['provider-success-at'] as string | undefined,
        ...account.nsldsPslfLoans.map((loan) => loan.nsldsUpdatedAt),
      ])
      .filter(Boolean) as string[];

    if (dates.length === 0) return null;

    dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return dates[0]!.slice(0, 10);
  }

  get loansWithProgress(): LoanProgress[] {
    const loans: LoanProgress[] = [];

    for (const account of this.nsldsAccounts) {
      if (!account.isActive) continue;

      for (const loan of account.nsldsPslfLoans) {
        const isPaidOff = loan.observation?.['is-paid-off'] ?? false;
        const qualified = loan.observation?.['forgiveness-program-qualified'] ?? 0;
        if (isPaidOff && qualified === 0) continue;
        const name = loan.name ?? `Loan ${loan.id ?? ''}`;
        const servicer = account.institutionName || undefined;
        const displayName = servicer ? `${name} - ${servicer}` : name;
        loans.push({
          id: loan.id ?? '',
          name,
          servicer,
          displayName,
          qualifyingPayments: qualified,
          totalRequired: this.TOTAL_REQUIRED_PAYMENTS,
          percentage: Math.min(Math.round((qualified / this.TOTAL_REQUIRED_PAYMENTS) * 100), 100),
          hasProgress: qualified > 0,
        });
      }
    }

    // Sort by most progress first
    loans.sort((a, b) => b.qualifyingPayments - a.qualifyingPayments);

    return loans;
  }

  get hasLoans() {
    return this.loansWithProgress.length > 0;
  }

  get primaryLoan() {
    return this.loansWithProgress[0];
  }

  get remainingLoans() {
    return this.loansWithProgress.slice(1);
  }

  get hasRemainingLoans() {
    return this.remainingLoans.length > 0;
  }

  toggleShowAllLoans = () => {
    this.showAllLoans = !this.showAllLoans;
  };

  get uploadQueryParams() {
    return { source: 'pslf', providers: 'nslds' };
  }

  <template>
    <Card @padding="lg" @roundedXl={{true}} data-test-qualifying-payment-tracker ...attributes>
      <Heading @level={{3}} @size="base" class="mb-3">
        {{t "pslf.payment_tracker.title"}}
      </Heading>

      {{#if this.hasLoans}}
        <div class="divide-y tio-divide-subtle">
          {{#let this.primaryLoan as |loan|}}
            {{#if loan}}
              <div class="py-3 first:pt-0 last:pb-0">
                <div class="tio-row-between mb-1">
                  <Text @size="sm" @weight="semibold">
                    {{loan.displayName}}
                  </Text>
                  {{#if loan.hasProgress}}
                    <Text @variant="muted" @size="sm">
                      {{loan.qualifyingPayments}}/{{loan.totalRequired}}
                      {{t "pslf.payment_tracker.payments"}}
                    </Text>
                  {{else}}
                    <Text @variant="muted" @size="sm" class="tio-row-extra-tight">
                      <PhClockCountdown @size={{16}} />
                      {{t "pslf.payment_tracker.no_progress"}}
                    </Text>
                  {{/if}}
                </div>
                <ProgressBar
                  @value={{loan.percentage}}
                  @color="violet"
                  @size="md"
                  @label={{loan.displayName}}
                />
              </div>
            {{/if}}
          {{/let}}

          {{#if this.hasRemainingLoans}}
            <Collapsible @isOpen={{this.showAllLoans}}>
              {{#each this.remainingLoans as |loan|}}
                <div class="py-3 last:pb-0">
                  <div class="tio-row-between mb-1">
                    <Text @size="sm" @weight="semibold">
                      {{loan.displayName}}
                    </Text>
                    {{#if loan.hasProgress}}
                      <Text @variant="muted" @size="sm">
                        {{loan.qualifyingPayments}}/{{loan.totalRequired}}
                        {{t "pslf.payment_tracker.payments"}}
                      </Text>
                    {{else}}
                      <Text @variant="muted" @size="sm" class="tio-row-extra-tight">
                        <PhClockCountdown @size={{16}} />
                        {{t "pslf.payment_tracker.no_progress"}}
                      </Text>
                    {{/if}}
                  </div>
                  <ProgressBar
                    @value={{loan.percentage}}
                    @color="violet"
                    @size="md"
                    @label={{loan.displayName}}
                  />
                </div>
              {{/each}}
            </Collapsible>
            <button
              type="button"
              class="tio-row-extra-tight text-sm tio-fg-brand hover:tio-fg-brand pt-3 cursor-pointer"
              data-test-toggle-remaining-loans
              {{on "click" this.toggleShowAllLoans}}
            >
              {{#if this.showAllLoans}}
                {{t "pslf.payment_tracker.show_fewer"}}
              {{else}}
                {{t "pslf.payment_tracker.show_all" count=this.remainingLoans.length}}
              {{/if}}
              <PhCaretDown
                @size={{14}}
                class="transition-transform {{if this.showAllLoans 'rotate-180'}}"
              />
            </button>
          {{/if}}
        </div>
      {{else}}
        {{! Empty state — single blank progress bar }}
        <div class="py-3">
          <div class="tio-row-between mb-1">
            <Text @size="sm" @weight="semibold">
              {{t "pslf.payment_tracker.title"}}
            </Text>
            <Text @variant="muted" @size="sm" class="tio-row-extra-tight">
              <PhClockCountdown @size={{16}} />
              {{t "pslf.payment_tracker.no_progress"}}
            </Text>
          </div>
          <ProgressBar @value={{0}} @color="violet" @size="md" />
        </div>
      {{/if}}

      {{#if this.nsldsLastUpdated}}
        <Text @variant="muted" @size="sm" @tag="p" class="mt-4">
          {{t "pslf.payment_tracker.last_updated" date=this.nsldsLastUpdated}}
        </Text>
      {{else}}
        <Button
          class="mt-4 self-start"
          @appearance="minimal"
          @intent="primary"
          data-test-upload-for-progress
          {{on
            "click"
            (transitionTo
              "authenticated.observability.upload" (hash queryParams=this.uploadQueryParams)
            )
          }}
        >
          {{t "pslf.payment_tracker.upload_prompt"}}
          <PhArrowRight @size={{16}} />
        </Button>
      {{/if}}
    </Card>
  </template>
}

export default QualifyingPaymentTracker;
